import { AIPlan, AIPlanStub } from 'api/interfaces/AIPlan'
import { AIPlanExecutiveSummarySection } from 'api/interfaces/AIPlanExecutiveSummarySection'
import { AIPlanMarketAnalysisSection, MarketAnalysisCustomSectionPosition } from 'api/interfaces/AIPlanMarketAnalysisSection'
import { AIPlanMediaPlanSection } from 'api/interfaces/AIPlanMediaPlanSection'
import { AIPlanPersonasSection } from 'api/interfaces/AIPlanPersonasSection'
import { AIPlanProjectionsSection } from 'api/interfaces/AIPlanProjectionsSection'
import { CompetitorProfile, GeographicCluster, MarketAnalysis, MarketChallenge } from 'api/interfaces/MarketAnalysis'
import { Persona } from 'api/interfaces/Persona'

import { defaultTransformFn, platformApi } from './platformApi'

const aiPlansTags = platformApi.enhanceEndpoints({ addTagTypes: ['AIPlan', 'AccountAIPlans', 'OrganizationAIPlans', 'AIPlanStub', 'AccountAIPlanStubs', 'AIPlanExecutiveSummarySection', 'AIPlanMediaPlanSection', 'AIPlanProjectionsSection', 'AIPlanPersonasSection', 'AIPlanMarketAnalysisSection'] })

type CreateAIPlanRequest = {
  organizationId: string
  params: {
    accountId: string
    blueprintId: string
    name: string
    outputFormat: string
    budgetMode: string
    monthlyBudget?: number
    highBudget?: number
    mediumBudget?: number
    lowBudget?: number
    channelMode: string
    selectedChannels?: string[]
    personaMode: string
    selectedPersonas?: string[]
    planDurationInMonths: number
    planStartDate: string
    additionalGuidance?: string
  }
}

type AIPlanStubCampaignBuilderRequest = {
  name: string
  shortSummary: string
  longSummary: string
  audienceSummary: string
  audienceDetails: string
  geoSummary: string
  geoDetails: string
  seasonalitySummary: string
  seasonalityDetails: string
  campaignType: string
  isOffAdCritter: boolean
  offAdCritterName?: string
  offAdCritterColor?: string
  intervalBudget: number
  retargetSite: boolean
  isTotalBudget: boolean
  startDate: string
  endDate?: string
  intervalUnit?: string
  intervalFrequency?: number
  intervalDuration?: number
  segmentTargets?: any[]
  audienceSegmentLooksUp?: string[]
  targetingType?: string
  regions?: string[]
  dmas?: string[]
  cities?: string[]
  zips?: any[]
  cpm?: number
  marginOfError?: number
  includeInProjections: boolean
  includeInGeoHeatmap: boolean
  keywords?: string[]
  keywordDescription?: string
  cpc?: number
}

export const aiPlansApi = aiPlansTags.injectEndpoints({
  endpoints: build => ({
    getAIPlans: build.query<AIPlan[], { organizationId: string; showHidden: boolean }>({
      query: ({ showHidden }) => ({
        url: `aiPlans`,
        method: 'GET',
        params: { showHidden },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'OrganizationAIPlans', id: `${request.organizationId}-${request.showHidden}` }]
    }),
    getAIPlan: build.query<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    getAIPlansByAccountId: build.query<AIPlan[], { accountId: string }>({
      query: ({ accountId }) => ({
        url: `aiPlans`,
        method: 'GET',
        params: { accountId },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AccountAIPlans', id: request.accountId }]
    }),
    beginCreateAIPlan: build.mutation<AIPlan[], CreateAIPlanRequest>({
      query: ({ params }) => ({
        url: `aiPlans`,
        method: 'POST',
        data: { ...params },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [...(result ?? []).map(p => ({ type: 'AIPlan' as const, id: p.id })), { type: 'AccountAIPlans', id: request.params.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    deleteAIPlan: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    unhideAIPlan: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/setHidden`,
        method: 'POST',
        data: { isHidden: false },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanEditName: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string; name: string }>({
      query: ({ aiPlanId, name }) => ({
        url: `aiPlans/${aiPlanId}/edit/name`,
        method: 'POST',
        data: { name },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ aiPlanId, name }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            draft.name = name
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanEditPlanDetails: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string; planSummary: string; planDetails: string }>({
      query: ({ aiPlanId, planSummary, planDetails }) => ({
        url: `aiPlans/${aiPlanId}/edit/planDetails`,
        method: 'POST',
        data: { summary: planSummary, details: planDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ aiPlanId, planSummary, planDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            draft.planSummary = planSummary
            draft.planDetails = planDetails
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanEditAudienceDetails: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string; audienceSummary: string; audienceDetails: string }>({
      query: ({ aiPlanId, audienceSummary, audienceDetails }) => ({
        url: `aiPlans/${aiPlanId}/edit/audienceDetails`,
        method: 'POST',
        data: { summary: audienceSummary, details: audienceDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ aiPlanId, audienceSummary, audienceDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            draft.audienceSummary = audienceSummary
            draft.audienceDetails = audienceDetails
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanEditGeoDetails: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string; geoSummary: string; geoDetails: string }>({
      query: ({ aiPlanId, geoSummary, geoDetails }) => ({
        url: `aiPlans/${aiPlanId}/edit/geoDetails`,
        method: 'POST',
        data: { summary: geoSummary, details: geoDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ aiPlanId, geoSummary, geoDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            draft.geoSummary = geoSummary
            draft.geoDetails = geoDetails
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanEditSeasonalityDetails: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string; seasonalitySummary: string; seasonalityDetails: string }>({
      query: ({ aiPlanId, seasonalitySummary, seasonalityDetails }) => ({
        url: `aiPlans/${aiPlanId}/edit/seasonalityDetails`,
        method: 'POST',
        data: { summary: seasonalitySummary, details: seasonalityDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ aiPlanId, seasonalitySummary, seasonalityDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            draft.seasonalitySummary = seasonalitySummary
            draft.seasonalityDetails = seasonalityDetails
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanEditMarketAnalysisSection: build.mutation<AIPlan, { aiPlanId: string; sectionName: string; data: Partial<MarketAnalysis[keyof MarketAnalysis]> }>({
      query: ({ aiPlanId, sectionName, data }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/${sectionName}`,
        method: 'PUT',
        data,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    aiPlanResetMarketAnalysis: build.mutation<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/edit/resetMarketAnalysis`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    aiPlanEditPersonas: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string; personas: Persona[] }>({
      query: ({ aiPlanId, personas }) => ({
        url: `aiPlans/${aiPlanId}/edit/personas`,
        method: 'POST',
        data: { personas },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ aiPlanId, personas }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            draft.personas = personas
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanRemovePersona: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string; personaName: string; personaRank: number }>({
      query: ({ aiPlanId, personaName, personaRank }) => ({
        url: `aiPlans/${aiPlanId}/edit/removePersona/${encodeURIComponent(personaName)}/${personaRank}`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (result, _error, request) => [{ type: 'AIPlan', id: result?.id }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanResetPersonas: build.mutation<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/edit/resetPersonas`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    getAIPlanStub: build.query<AIPlanStub, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlanStubs/${aiPlanId}/aiPlanStub`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (result, _error, request) => [
        { type: 'AIPlanStub', id: request.aiPlanId },
        { type: 'AIPlanStub', id: result?.id }
      ]
    }),
    getAIPlanStubsForAccount: build.query<AIPlanStub[], { accountId: string }>({
      query: ({ accountId }) => ({
        url: `aiPlanStubs/${accountId}/forAccount`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AccountAIPlanStubs', id: request.accountId }]
    }),
    addAIPlanStubCampaignBuilder: build.mutation<AIPlanStub, { aiPlanStubId: string; request: AIPlanStubCampaignBuilderRequest }>({
      query: ({ aiPlanStubId, request }) => ({
        url: `aiPlanStubs/${aiPlanStubId}/edit/addCampaignBuilder`,
        method: 'POST',
        data: request,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        const { data: stub } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanStub', { aiPlanId: stub.aiPlanId }, draft => {
            Object.assign(draft, stub)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'AIPlanStub', id: _result?.aiPlanId },
        { type: 'AIPlanStub', id: request.aiPlanStubId }
      ]
    }),
    editAIPlanStubCampaignBuilder: build.mutation<AIPlanStub, { aiPlanStubId: string; campaignBuilderId: string; aiPlanId: string; request: AIPlanStubCampaignBuilderRequest }>({
      query: ({ aiPlanStubId, campaignBuilderId, request }) => ({
        url: `aiPlanStubs/${aiPlanStubId}/edit/editCampaignBuilder/${campaignBuilderId}`,
        method: 'POST',
        data: request,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        const { data: stub } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanStub', { aiPlanId: stub.aiPlanId }, draft => {
            Object.assign(draft, stub)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'AIPlanStub', id: request.aiPlanId },
        { type: 'AIPlan', id: request.aiPlanId },
        { type: 'AIPlanStub', id: request.aiPlanStubId }
      ]
    }),
    removeAIPlanStubCampaignBuilder: build.mutation<AIPlanStub, { aiPlanId: string; aiPlanStubId: string; campaignBuilderId: string }>({
      query: ({ aiPlanStubId, campaignBuilderId }) => ({
        url: `aiPlanStubs/${aiPlanStubId}/edit/removeCampaignBuilder/${campaignBuilderId}`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        const { data: stub } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanStub', { aiPlanId: stub.aiPlanId }, draft => {
            Object.assign(draft, stub)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'AIPlanStub', id: request.aiPlanId },
        { type: 'AIPlanStub', id: request.aiPlanStubId }
      ]
    }),
    copyAIPlanToDsp: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/copyToDsp`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }, { type: 'AIPlanStub', id: request.aiPlanId }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiPlanResetPlanDetails: build.mutation<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/edit/resetPlanDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    aiPlanResetAudienceDetails: build.mutation<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/edit/resetAudienceDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    aiPlanResetGeoDetails: build.mutation<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/edit/resetGeoDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    aiPlanResetSeasonalityDetails: build.mutation<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/edit/resetSeasonalityDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    aiPlanResetAll: build.mutation<AIPlan, { organizationId: string; accountId: string; aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/edit/resetAll`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, (draft: AIPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    addAIPlanCompetitorProfile: build.mutation<AIPlan, { aiPlanId: string; competitorProfile: CompetitorProfile }>({
      query: ({ aiPlanId, competitorProfile }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/competitiveLandscape/competitorProfile`,
        method: 'POST',
        data: competitorProfile,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    editAIPlanCompetitorProfile: build.mutation<AIPlan, { aiPlanId: string; index: number; competitorProfile: CompetitorProfile }>({
      query: ({ aiPlanId, index, competitorProfile }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/competitiveLandscape/competitorProfile/${index}`,
        method: 'PUT',
        data: competitorProfile,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    removeAIPlanCompetitorProfile: build.mutation<AIPlan, { aiPlanId: string; index: number }>({
      query: ({ aiPlanId, index }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/competitiveLandscape/competitorProfile/${index}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    addAIPlanGeographicCluster: build.mutation<AIPlan, { aiPlanId: string; geographicCluster: GeographicCluster }>({
      query: ({ aiPlanId, geographicCluster }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/geographicOpportunities/geographicCluster`,
        method: 'POST',
        data: geographicCluster,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    editAIPlanGeographicCluster: build.mutation<AIPlan, { aiPlanId: string; index: number; geographicCluster: GeographicCluster }>({
      query: ({ aiPlanId, index, geographicCluster }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/geographicOpportunities/geographicCluster/${index}`,
        method: 'PUT',
        data: geographicCluster,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    removeAIPlanGeographicCluster: build.mutation<AIPlan, { aiPlanId: string; index: number }>({
      query: ({ aiPlanId, index }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/geographicOpportunities/geographicCluster/${index}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    addAIPlanMarketChallenge: build.mutation<AIPlan, { aiPlanId: string; marketChallenge: MarketChallenge }>({
      query: ({ aiPlanId, marketChallenge }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/coreChallengesInMarket/marketChallenge`,
        method: 'POST',
        data: marketChallenge,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    editAIPlanMarketChallenge: build.mutation<AIPlan, { aiPlanId: string; index: number; marketChallenge: MarketChallenge }>({
      query: ({ aiPlanId, index, marketChallenge }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/coreChallengesInMarket/marketChallenge/${index}`,
        method: 'PUT',
        data: marketChallenge,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    removeAIPlanMarketChallenge: build.mutation<AIPlan, { aiPlanId: string; index: number }>({
      query: ({ aiPlanId, index }) => ({
        url: `aiPlans/${aiPlanId}/edit/marketAnalysis/coreChallengesInMarket/marketChallenge/${index}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    getAIPlanExecutiveSummarySection: build.query<AIPlanExecutiveSummarySection, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    updateAIPlanIntroductionSection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          introduction: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    updateAIPlanChannelUtilizationSection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          channelUtilization: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    updateAIPlanAudienceStrategySection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          audienceStrategy: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    updateAIPlanGeographicTargetingSection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          geographicTargeting: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    updateAIPlanBudgetSection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          budget: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    hideAIPlanExecutiveSummarySection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; sectionType: 'introduction' | 'channelUtilization' | 'audienceStrategy' | 'geographicTargeting' | 'budget' }>({
      query: ({ aiPlanId, sectionType }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    addAIPlanCustomSection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; position: 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    updateAIPlanCustomSection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; position: 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `aiPlans/${aiPlanId}/executiveSummarySection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    deleteAIPlanCustomSection: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; position: 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'; existingSections: any[]; sectionIndex: number }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `aiPlans/${aiPlanId}/executiveSummarySection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    updateAIPlanPageTitle: build.mutation<AIPlanExecutiveSummarySection, { aiPlanId: string; pageTitle: string }>({
      query: ({ aiPlanId, pageTitle }) => ({
        url: `aiPlans/${aiPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanExecutiveSummarySection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanExecutiveSummarySection', id: request.aiPlanId }]
    }),
    getAIPlanMediaPlanSection: build.query<AIPlanMediaPlanSection, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/mediaPlanSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    updateAIPlanMediaPlanBudgetSection: build.mutation<AIPlanMediaPlanSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          budget: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMediaPlanSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    updateAIPlanMediaPlanCampaignDetailsTitle: build.mutation<AIPlanMediaPlanSection, { aiPlanId: string; campaignDetailsTitle: string }>({
      query: ({ aiPlanId, campaignDetailsTitle }) => ({
        url: `aiPlans/${aiPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          campaignDetailsTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMediaPlanSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    hideAIPlanMediaPlanSection: build.mutation<AIPlanMediaPlanSection, { aiPlanId: string; sectionType: 'budget' }>({
      query: ({ aiPlanId, sectionType }) => ({
        url: `aiPlans/${aiPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMediaPlanSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    addAIPlanMediaPlanCustomSection: build.mutation<AIPlanMediaPlanSection, { aiPlanId: string; position: 'aboveCampaignTimeline' | 'aboveCampaignDetails'; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMediaPlanSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    updateAIPlanMediaPlanCustomSection: build.mutation<AIPlanMediaPlanSection, { aiPlanId: string; position: 'aboveCampaignTimeline' | 'aboveCampaignDetails'; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `aiPlans/${aiPlanId}/mediaPlanSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMediaPlanSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    deleteAIPlanMediaPlanCustomSection: build.mutation<AIPlanMediaPlanSection, { aiPlanId: string; position: 'aboveCampaignTimeline' | 'aboveCampaignDetails'; existingSections: any[]; sectionIndex: number }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `aiPlans/${aiPlanId}/mediaPlanSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMediaPlanSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    updateAIPlanMediaPlanPageTitle: build.mutation<AIPlanMediaPlanSection, { aiPlanId: string; pageTitle: string }>({
      query: ({ aiPlanId, pageTitle }) => ({
        url: `aiPlans/${aiPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMediaPlanSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMediaPlanSection', id: request.aiPlanId }]
    }),
    getAIPlanProjectionsSection: build.query<AIPlanProjectionsSection, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsCpmAndImpressionsSection: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          cpmAndImpressions: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsAuctionBasedPlacementsSection: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          auctionBasedPlacements: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsCtvPlacementCategoriesSection: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          ctvPlacementCategories: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsInternetAdPlacementsSection: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          internetAdPlacements: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsGeographicDistributionSection: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          geographicDistribution: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsBillboardLocationsSection: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          billboardLocations: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsPageTitle: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; pageTitle: string }>({
      query: ({ aiPlanId, pageTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    updateAIPlanProjectionsWhereAdsAppearTitle: build.mutation<AIPlanProjectionsSection, { aiPlanId: string; whereAdsAppearTitle: string }>({
      query: ({ aiPlanId, whereAdsAppearTitle }) => ({
        url: `aiPlans/${aiPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          whereAdsAppearTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanProjectionsSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanProjectionsSection', id: request.aiPlanId }]
    }),
    getAIPlanPersonasSection: build.query<AIPlanPersonasSection, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/personasSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanPersonasSection', id: request.aiPlanId }]
    }),
    updateAIPlanPersonasIntroductionSection: build.mutation<AIPlanPersonasSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/personasSection`,
        method: 'PUT',
        data: {
          introduction: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanPersonasSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanPersonasSection', id: request.aiPlanId }]
    }),
    hideAIPlanPersonasSection: build.mutation<AIPlanPersonasSection, { aiPlanId: string; sectionType: 'introduction' }>({
      query: ({ aiPlanId, sectionType }) => ({
        url: `aiPlans/${aiPlanId}/personasSection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanPersonasSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanPersonasSection', id: request.aiPlanId }]
    }),
    addAIPlanPersonasCustomSection: build.mutation<AIPlanPersonasSection, { aiPlanId: string; position: 'abovePersonaList'; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/personasSection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanPersonasSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanPersonasSection', id: request.aiPlanId }]
    }),
    updateAIPlanPersonasCustomSection: build.mutation<AIPlanPersonasSection, { aiPlanId: string; position: 'abovePersonaList'; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `aiPlans/${aiPlanId}/personasSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanPersonasSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanPersonasSection', id: request.aiPlanId }]
    }),
    deleteAIPlanPersonasCustomSection: build.mutation<AIPlanPersonasSection, { aiPlanId: string; position: 'abovePersonaList'; existingSections: any[]; sectionIndex: number }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `aiPlans/${aiPlanId}/personasSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanPersonasSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanPersonasSection', id: request.aiPlanId }]
    }),
    updateAIPlanPersonasPageTitle: build.mutation<AIPlanPersonasSection, { aiPlanId: string; pageTitle: string }>({
      query: ({ aiPlanId, pageTitle }) => ({
        url: `aiPlans/${aiPlanId}/personasSection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanPersonasSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanPersonasSection', id: request.aiPlanId }]
    }),
    getAIPlanMarketAnalysisSection: build.query<AIPlanMarketAnalysisSection, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/marketAnalysisSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AIPlanMarketAnalysisSection', id: request.aiPlanId }]
    }),
    updateAIPlanMarketAnalysisSectionTitle: build.mutation<AIPlanMarketAnalysisSection, { aiPlanId: string; titleKey: string; titleValue: string }>({
      query: ({ aiPlanId, titleKey, titleValue }) => ({
        url: `aiPlans/${aiPlanId}/marketAnalysisSection`,
        method: 'PUT',
        data: { [titleKey]: titleValue },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMarketAnalysisSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMarketAnalysisSection', id: request.aiPlanId }]
    }),
    updateAIPlanMarketAnalysisIntroductionSection: build.mutation<AIPlanMarketAnalysisSection, { aiPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/marketAnalysisSection`,
        method: 'PUT',
        data: {
          introduction: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMarketAnalysisSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMarketAnalysisSection', id: request.aiPlanId }]
    }),
    hideAIPlanMarketAnalysisIntroductionSection: build.mutation<AIPlanMarketAnalysisSection, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/marketAnalysisSection`,
        method: 'PUT',
        data: {
          introduction: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMarketAnalysisSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMarketAnalysisSection', id: request.aiPlanId }]
    }),
    addAIPlanMarketAnalysisCustomSection: build.mutation<AIPlanMarketAnalysisSection, { aiPlanId: string; position: MarketAnalysisCustomSectionPosition; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, title, content, showTitle }) => ({
        url: `aiPlans/${aiPlanId}/marketAnalysisSection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMarketAnalysisSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMarketAnalysisSection', id: request.aiPlanId }]
    }),
    updateAIPlanMarketAnalysisCustomSection: build.mutation<AIPlanMarketAnalysisSection, { aiPlanId: string; position: MarketAnalysisCustomSectionPosition; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `aiPlans/${aiPlanId}/marketAnalysisSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMarketAnalysisSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMarketAnalysisSection', id: request.aiPlanId }]
    }),
    deleteAIPlanMarketAnalysisCustomSection: build.mutation<AIPlanMarketAnalysisSection, { aiPlanId: string; position: MarketAnalysisCustomSectionPosition; existingSections: any[]; sectionIndex: number }>({
      query: ({ aiPlanId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `aiPlans/${aiPlanId}/marketAnalysisSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      async onQueryStarted({ aiPlanId }, { dispatch, queryFulfilled }) {
        const { data: section } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlanMarketAnalysisSection', { aiPlanId }, draft => {
            Object.assign(draft, section)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlanMarketAnalysisSection', id: request.aiPlanId }]
    }),
    copyAIPlanCampaignToDsp: build.mutation<{ aiPlanId: string; masterCampaignId: string; dspCampaignId: string; copyStatus: string }, { organizationId: string; accountId: string; aiPlanId: string; campaignBuilderId: string }>({
      query: ({ aiPlanId, campaignBuilderId }) => ({
        url: `aiPlans/${aiPlanId}/campaigns/${campaignBuilderId}/copyToDsp`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }, { type: 'AIPlanStub', id: request.aiPlanId }, { type: 'AccountAIPlans', id: request.accountId }, { type: 'OrganizationAIPlans' }]
    }),
    aiEditAIPlan: build.mutation<AIPlan, { entityId: string; targetType: string; targetId: string; prompt: string; sectionName?: string; fieldKey?: string; campaignBuilderId?: string; constraintCampaignType?: string; constraintStartDate?: string; constraintDuration?: string; constraintBudget?: string; offAdCritterPlatformName?: string; deepThink?: boolean; execSummarySections?: Record<string, { title: string; content: string }> }>({
      query: ({ entityId, targetType, targetId, prompt, sectionName, fieldKey, campaignBuilderId, constraintCampaignType, constraintStartDate, constraintDuration, constraintBudget, offAdCritterPlatformName, deepThink, execSummarySections }) => ({
        url: `aiPlans/${entityId}/aiEdit`,
        method: 'POST',
        data: { targetType, targetId, prompt, sectionName, fieldKey, campaignBuilderId, constraintCampaignType, constraintStartDate, constraintDuration, constraintBudget, offAdCritterPlatformName, deepThink: deepThink ?? false, execSummarySections },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ entityId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled
        dispatch(
          aiPlansApi.util.updateQueryData('getAIPlan', { aiPlanId: entityId }, draft => {
            Object.assign(draft, plan)
          })
        )
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'AIPlan', id: request.entityId },
        { type: 'AIPlanStub', id: request.entityId },
        { type: 'AIPlanPersonasSection', id: request.entityId },
        { type: 'AIPlanMarketAnalysisSection', id: request.entityId },
        { type: 'AIPlanExecutiveSummarySection', id: request.entityId }
      ]
    }),
    getOffAdCritterPlatforms: build.query<{ name: string; type: string; color: string }[], void>({
      query: () => ({
        url: `aiPlans/offAdCritterPlatforms`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn
    }),
    createAIPlanPublicId: build.mutation<AIPlan, { aiPlanId: string }>({
      query: ({ aiPlanId }) => ({
        url: `aiPlans/${aiPlanId}/createAIPlanPublicId`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    }),
    updateAIPlanClientPreviewTabs: build.mutation<AIPlan, { aiPlanId: string; showExecutiveSummary: boolean; showMarketAnalysis: boolean; showPersonas: boolean; showMediaPlan: boolean; showProjections: boolean }>({
      query: ({ aiPlanId, ...data }) => ({
        url: `aiPlans/${aiPlanId}/clientPreviewTabs`,
        method: 'PUT',
        data,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'AIPlan', id: request.aiPlanId }]
    })
  })
})

export const {
  useGetAIPlansQuery,
  useGetAIPlanQuery,
  useGetAIPlansByAccountIdQuery,
  useBeginCreateAIPlanMutation,
  useDeleteAIPlanMutation,
  useAiPlanEditNameMutation,
  useAiPlanEditPlanDetailsMutation,
  useAiPlanEditAudienceDetailsMutation,
  useAiPlanEditGeoDetailsMutation,
  useAiPlanEditSeasonalityDetailsMutation,
  useAiPlanEditMarketAnalysisSectionMutation,
  useAiPlanResetMarketAnalysisMutation,
  useAiPlanEditPersonasMutation,
  useAiPlanRemovePersonaMutation,
  useAiPlanResetPersonasMutation,
  useGetAIPlanStubQuery,
  useGetAIPlanStubsForAccountQuery,
  useAddAIPlanStubCampaignBuilderMutation,
  useEditAIPlanStubCampaignBuilderMutation,
  useRemoveAIPlanStubCampaignBuilderMutation,
  useCopyAIPlanToDspMutation,
  useAiPlanResetPlanDetailsMutation,
  useAiPlanResetAudienceDetailsMutation,
  useAiPlanResetGeoDetailsMutation,
  useAiPlanResetSeasonalityDetailsMutation,
  useAiPlanResetAllMutation,
  useAddAIPlanCompetitorProfileMutation,
  useEditAIPlanCompetitorProfileMutation,
  useRemoveAIPlanCompetitorProfileMutation,
  useAddAIPlanGeographicClusterMutation,
  useEditAIPlanGeographicClusterMutation,
  useRemoveAIPlanGeographicClusterMutation,
  useAddAIPlanMarketChallengeMutation,
  useEditAIPlanMarketChallengeMutation,
  useRemoveAIPlanMarketChallengeMutation,
  useGetAIPlanExecutiveSummarySectionQuery,
  useUpdateAIPlanIntroductionSectionMutation,
  useUpdateAIPlanChannelUtilizationSectionMutation,
  useUpdateAIPlanAudienceStrategySectionMutation,
  useUpdateAIPlanGeographicTargetingSectionMutation,
  useUpdateAIPlanBudgetSectionMutation,
  useHideAIPlanExecutiveSummarySectionMutation,
  useAddAIPlanCustomSectionMutation,
  useUpdateAIPlanCustomSectionMutation,
  useDeleteAIPlanCustomSectionMutation,
  useUpdateAIPlanPageTitleMutation,
  useGetAIPlanMediaPlanSectionQuery,
  useUpdateAIPlanMediaPlanBudgetSectionMutation,
  useUpdateAIPlanMediaPlanCampaignDetailsTitleMutation,
  useHideAIPlanMediaPlanSectionMutation,
  useAddAIPlanMediaPlanCustomSectionMutation,
  useUpdateAIPlanMediaPlanCustomSectionMutation,
  useDeleteAIPlanMediaPlanCustomSectionMutation,
  useUpdateAIPlanMediaPlanPageTitleMutation,
  useGetAIPlanProjectionsSectionQuery,
  useUpdateAIPlanProjectionsCpmAndImpressionsSectionMutation,
  useUpdateAIPlanProjectionsAuctionBasedPlacementsSectionMutation,
  useUpdateAIPlanProjectionsCtvPlacementCategoriesSectionMutation,
  useUpdateAIPlanProjectionsInternetAdPlacementsSectionMutation,
  useUpdateAIPlanProjectionsGeographicDistributionSectionMutation,
  useUpdateAIPlanProjectionsBillboardLocationsSectionMutation,
  useUpdateAIPlanProjectionsPageTitleMutation,
  useUpdateAIPlanProjectionsWhereAdsAppearTitleMutation,
  useGetAIPlanPersonasSectionQuery,
  useUpdateAIPlanPersonasIntroductionSectionMutation,
  useHideAIPlanPersonasSectionMutation,
  useAddAIPlanPersonasCustomSectionMutation,
  useUpdateAIPlanPersonasCustomSectionMutation,
  useDeleteAIPlanPersonasCustomSectionMutation,
  useUpdateAIPlanPersonasPageTitleMutation,
  useGetAIPlanMarketAnalysisSectionQuery,
  useUpdateAIPlanMarketAnalysisSectionTitleMutation,
  useUpdateAIPlanMarketAnalysisIntroductionSectionMutation,
  useHideAIPlanMarketAnalysisIntroductionSectionMutation,
  useAddAIPlanMarketAnalysisCustomSectionMutation,
  useUpdateAIPlanMarketAnalysisCustomSectionMutation,
  useDeleteAIPlanMarketAnalysisCustomSectionMutation,
  useCopyAIPlanCampaignToDspMutation,
  useAiEditAIPlanMutation,
  useGetOffAdCritterPlatformsQuery,
  useCreateAIPlanPublicIdMutation,
  useUpdateAIPlanClientPreviewTabsMutation,
  useUnhideAIPlanMutation
} = aiPlansApi
